import dayjs from 'dayjs';
import { ClockConfigType, ConfigRulesProps } from '../config/letters/config.types';

type TimePositionTypes =
  | 'fiveAfter'
  | 'tenAfter'
  | 'quarterAfter'
  | 'twentyAfter'
  | 'fiveBeforeHalf'
  | 'half'
  | 'fiveAfterHalf'
  | 'twentyBefore'
  | 'quarterBefore'
  | 'tenBefore'
  | 'fiveBefore';

// Cache for position map to avoid repeated calculations
const MINUTE_TO_POSITION_MAP: (TimePositionTypes | null)[] = [
  null,
  null,
  null,
  'fiveAfter',
  'fiveAfter',
  'fiveAfter',
  'fiveAfter',
  'fiveAfter',
  'tenAfter',
  'tenAfter',
  'tenAfter',
  'tenAfter',
  'tenAfter',
  'quarterAfter',
  'quarterAfter',
  'quarterAfter',
  'quarterAfter',
  'quarterAfter',
  'twentyAfter',
  'twentyAfter',
  'twentyAfter',
  'twentyAfter',
  'twentyAfter',
  'fiveBeforeHalf',
  'fiveBeforeHalf',
  'fiveBeforeHalf',
  'fiveBeforeHalf',
  'fiveBeforeHalf',
  'half',
  'half',
  'half',
  'half',
  'half',
  'fiveAfterHalf',
  'fiveAfterHalf',
  'fiveAfterHalf',
  'fiveAfterHalf',
  'fiveAfterHalf',
  'twentyBefore',
  'twentyBefore',
  'twentyBefore',
  'twentyBefore',
  'twentyBefore',
  'quarterBefore',
  'quarterBefore',
  'quarterBefore',
  'quarterBefore',
  'quarterBefore',
  'tenBefore',
  'tenBefore',
  'tenBefore',
  'tenBefore',
  'tenBefore',
  'fiveBefore',
  'fiveBefore',
  'fiveBefore',
  'fiveBefore',
  'fiveBefore',
  'fiveBefore',
  'fiveBefore',
  'fiveBefore',
];

// Cache for configuration-dependent positions
const POSITION_NEXT_HOUR_CACHE: Record<ClockConfigType, Record<TimePositionTypes, boolean>> = {
  german: {
    fiveAfter: false,
    tenAfter: false,
    quarterAfter: false,
    twentyAfter: false,
    fiveBeforeHalf: true,
    half: true,
    fiveAfterHalf: true,
    twentyBefore: true,
    quarterBefore: true,
    tenBefore: true,
    fiveBefore: true,
  },
  english: {
    fiveAfter: false,
    tenAfter: false,
    quarterAfter: false,
    twentyAfter: false,
    fiveBeforeHalf: false,
    half: false,
    fiveAfterHalf: true,
    twentyBefore: true,
    quarterBefore: true,
    tenBefore: true,
    fiveBefore: true,
  },
  russian: {
    fiveAfter: false,
    tenAfter: false,
    quarterAfter: false,
    twentyAfter: false,
    fiveBeforeHalf: false,
    half: false,
    fiveAfterHalf: false,
    twentyBefore: false,
    quarterBefore: false,
    tenBefore: false,
    fiveBefore: false,
  },
};

/**
 * Calculates the active character positions based on the current time and configuration
 * @param config Configuration rules for the clock
 * @param configType Type of configuration (german, english, russian)
 * @returns Array with active character positions
 */
const handleActiveChars = (config: ConfigRulesProps, configType: ClockConfigType): number[] => {
  const now = dayjs();
  const minutes = now.minute();
  const hour = now.hour();
  const isExactHour = minutes < 3;
  const timePosition = MINUTE_TO_POSITION_MAP[minutes] ?? null;

  // Start with start positions
  const activeChars: number[] = [...config.start];

  // Add end positions if it's an exact hour
  if (isExactHour) {
    activeChars.push(...config.end);
  }

  // Add time position markers (e.g. "quarter past", "half", etc.)
  if (timePosition) {
    activeChars.push(...config[timePosition]);
  }

  // Determine the hour to display
  const positionNeedsNextHour = POSITION_NEXT_HOUR_CACHE[configType];
  const displayHour = timePosition && positionNeedsNextHour[timePosition] ? (hour + 1) % 24 : hour;

  // Hour mappings
  const hourMappings = [
    config.twelve,
    config.one,
    config.two,
    config.three,
    config.four,
    config.five,
    config.six,
    config.seven,
    config.eight,
    config.nine,
    config.ten,
    config.eleven,
  ];

  const hourIndex = displayHour % 12;

  // Special case for "one/a" in certain languages
  if (hourIndex === 1 && !isExactHour && config.oneExt) {
    activeChars.push(...config.oneExt);
  } else {
    activeChars.push(...hourMappings[hourIndex]);
  }

  return activeChars;
};

export default handleActiveChars;
